import { get, forEach, uniqBy } from 'lodash';
import { comparableDate } from '../../../common/formattedDate';

const now = comparableDate(new Date());

export default function PerformanceDetail(responses) {
  const performanceMapping = {
    inv_no: 'Details.Id',
    description: 'Details.Description',
    type: 'Details.R',
    text1: 'Details.Text1',
    text2: 'Details.Text2',
    text3: 'Details.Text3',
    text4: 'Details.Text4',
    perf_dt: 'Details.Date',
    composerfn: null,
    composermn: null,
    composerln: null,
    facility_desc: 'Details.Facility.Description',
    on_sale_ind: false,
    performance_type: 'Details.Type.Description',
    perf_type_id: 'Details.Type.Id',
    seat_ind: 'ModeOfSale.SeatIndicator',
    print_ind: 'ModeOfSale.PrintIndicator',
    zmap_no: 'Details.ZoneMap.Id',
    facility_no: 'Details.Facility.Id',
    prod_season_no: 'Details.ProductionSeason.Id',
    season_no: 'Details.Season.Id',
    season_desc: 'Details.Season.Description',
    start_dt: null,
    end_dt: null,
    perf_status: 'Details.Status.Id',
    perf_status_desc: 'Details.Status.Description',
    time_slot: 'Details.TimeSlot.Id',
    time_slot_desc: 'Details.TimeSlot.Description',
    ga_ind: null,
  };

  const priceMapping = {
    perf_no: 'Price.PerformanceId',
    pkg_no: 0,
    zone_no: 'Price.ZoneId',
    price_type: 'Price.PriceTypeId',
    price: 'Price.Price',
    base_price: 'Price.IsBase',
    description: 'ZoneAvailability.Zone.Description',
    available: false,
    avail_count: 'ZoneAvailability.AvailableCount',
    zone_group_no: 'ZoneAvailability.Zone.ZoneGroup.Id',
    zone_group_desc: 'ZoneAvailability.Zone.ZoneGroup.Description',
  };

  const priceTypeMapping = {
    price_type: 'PriceTypeId',
    description: 'Description',
    short_desc: 'ShortDescription',
    alias_description: 'AliasDescription',
    category: null,
    def_price_type: 'DefaultIndicator',
    promo: 'Offer',
  };

  const allPriceMapping = {
    zone_no: 'Price.ZoneId',
    zone_group_no: 'ZoneAvailability.Zone.ZoneGroup.Id',
    zone_group_desc: 'ZoneAvailability.Zone.ZoneGroup.Description',
    price_type: 'Price.PriceTypeId',
    price: 'Price.Price',
    base_price: 'Price.IsBase',
    description: 'ZoneAvailability.Zone.Description',
    available: false,
    avail_count: 'ZoneAvailability.AvailableCount',
    def_price_type: false,
    price_type_desc: 'PriceType.Description',
    price_type_alias_desc: 'PriceType.AliasDescription',
    rank: 'PriceType.Rank',
    offer: 'Price.Offer',
  };

  const webContentMapping = {
    orig_inv_no: 'WebContents[_I_].Owner.ElementId',
    inv_no: 'RequestedOwner.ElementId',
    inv_type: 'R',
    content_type: 'WebContents[_I_].Type.Id',
    content_type_desc: 'WebContents[_I_].Type.Description',
    content_value: 'WebContents[_I_].Value',
  };

  const mapped = {
    Performance: {},
    Price: [],
    Credit: [],
    PriceType: [],
    AllPrice: [],
    WebContent: [],
  };

  const [
    detailsResponse,
    modesOfSaleResponse,
    pricesResponse,
    performancePriceTypesResponse,
    packagePriceTypesResponse,
    zoneAvailabilitiesResponse,
    webContentsResponse,
  ] = responses;

  const mosDetails = {
    Details: detailsResponse.result,
    ModeOfSale: modesOfSaleResponse.result.length ? modesOfSaleResponse.result[0] : null,
  };

  const priceTypesResponse = [].concat(performancePriceTypesResponse.result, packagePriceTypesResponse.result);

  forEach(performanceMapping, (property, key) => {
    if (key === 'type') {
      mapped.Performance[key] = property;
    } else if (key === 'start_dt') {
      const startDate = get(mosDetails, 'ModeOfSale.StartDateTime', null);

      if (startDate) {
        mapped.Performance[key] = startDate;
      } else {
        mapped.Performance[key] = get(mosDetails, 'Details.DefaultStartSaleDateTime', null);
      }
    } else if (key === 'end_dt') {
      const endDate = get(mosDetails, 'ModeOfSale.EndDateTime', null);

      if (endDate) {
        mapped.Performance[key] = endDate;
      } else {
        mapped.Performance[key] = get(mosDetails, 'Details.DefaultEndSaleDateTime', null);
      }
    } else if (key === 'on_sale_ind') {
      const availSaleIndicator = get(mosDetails, 'Details.AvailSaleIndicator', false);

      const startDate = get(mosDetails, 'ModeOfSale.StartDateTime', null);
      const endDate = get(mosDetails, 'ModeOfSale.EndDateTime', null);

      if (!startDate || !endDate) {
        mapped.Performance[key] = availSaleIndicator;
      } else {
        mapped.Performance[key] = (
          availSaleIndicator &&
          comparableDate(startDate) < now &&
          comparableDate(endDate) > now
        );
      }
    } else {
      mapped.Performance[key] = get(mosDetails, property, null);
    }
  });

  const defaultPriceType = priceTypesResponse.find(type => {
    return type.DefaultIndicator === true;
  });

  if (defaultPriceType) {
    const defaultPrices = pricesResponse.result.filter(price => {
      return price.Enabled === true && price.PriceTypeId === defaultPriceType.PriceTypeId;
    });

    defaultPrices.forEach(defaultPrice => {
      const details = {
        Price: defaultPrice,
        ZoneAvailability: zoneAvailabilitiesResponse.result.find(zone => {
          return zone.Zone.Id === defaultPrice.ZoneId;
        }),
      };

      if (!details.ZoneAvailability) {
        return;
      }

      const price = {};

      forEach(priceMapping, (property, key) => {
        if (key === 'pkg_no') {
          price[key] = property;
        } else if (key === 'available') {
          price[key] = !!details.ZoneAvailability.AvailableCount;
        } else {
          price[key] = get(details, property, null);
        }
      });

      mapped.Price.push(price);
    });
  }

  const uniquePriceTypes = uniqBy(priceTypesResponse, 'PriceTypeId');

  pricesResponse.result.forEach(allPrice => {
    if (allPrice.Enabled === false) {
      return;
    }

    const details = {
      Price: allPrice,
      ZoneAvailability: zoneAvailabilitiesResponse.result.find(zone => {
        return zone.Zone.Id === allPrice.ZoneId;
      }),
      PriceType: uniquePriceTypes.find(type => {
        return type.PriceTypeId === allPrice.PriceTypeId;
      }),
    };

    if (!details.ZoneAvailability || !details.PriceType) {
      return;
    }

    const price = {};

    forEach(allPriceMapping, (property, key) => {
      if (key === 'pkg_no') {
        price[key] = property;
      } else if (key === 'available') {
        price[key] = !!details.ZoneAvailability.AvailableCount;
      } else if (key === 'def_price_type') {
        price[key] = defaultPriceType && details.PriceType.PriceTypeId === defaultPriceType.PriceTypeId;
      } else {
        price[key] = get(details, property, null);
      }
    });

    mapped.AllPrice.push(price);
  });

  uniquePriceTypes.forEach(uniquePriceType => {
    const priceType = {};

    forEach(priceTypeMapping, (property, key) => {
      if (property === null) {
        priceType[key] = null;
      } else {
        priceType[key] = get(uniquePriceType, property, null);
      }
    });

    mapped.PriceType.push(priceType);
  });

  const webContents = get(webContentsResponse, 'result[0].WebContents') || [];

  for (let i = 0; i < webContents.length; i++) {
    const webContent = {};

    forEach(webContentMapping, (property, key) => {
      webContent[key] = key === 'inv_type' ? property : get(webContentsResponse.result[0], property.replace('_I_', i), null);
    });

    mapped.WebContent.push(webContent);
  }

  return mapped;
}



// WEBPACK FOOTER //
// ./src/javascripts/lib/tessitura/REST/maps/PerformanceDetail.js